import { createApolloClient } from "../../../providers/ApolloClientFactory";
import { organizationStore } from "../../../store/organization";
import { GET_ORDER_AHEAD_LOCATIONS } from "./queries";
import {
  Link,
  LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { eventStore } from "../../../store/event";
import moment from "moment";
import { usePermissions } from "../../auth/api";
import { canManageOrderAhead } from "../../order-ahead/access";
import { Breadcrumbs } from "../../../components/Breadcrumbs";
import { Input } from "../../../components/inputs/Input";
import { PrimaryButton, SecondaryButton } from "../../../components/Button";
import { userStore } from "../../../store/user";
import Switch from "react-switch";
import { OrderAheadLocation } from "../../../types/orderAhead";

const client = createApolloClient();
const { getState } = userStore;
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventStore } = eventStore;

const buildSearch = (search: string) => [
  {
    name: {
      _ilike: `%${search}%`,
    },
  },
];

export const orderAheadLocationsListLoader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const search = url.searchParams.get("search");
  const rawOrganizationId = getOrganizationState().organizationId;
  const organizationId = parseInt(
    rawOrganizationId !== null ? rawOrganizationId.toString() : "",
    10
  );
  const eventId = getEventStore().eventId;
  const user = getState().user;

  let where: any = {
    _or: search ? buildSearch(search) : undefined,
  };
  if (organizationId && organizationId !== 0) {
    where = {
      ...where,
      event: {
        organization_id: {
          _eq: organizationId,
        },
      },
    };
  }
  if (eventId) {
    where = {
      ...where,
      event_id: {
        _eq: eventId,
      },
    };
  } else if (user?.events) {
    where = {
      ...where,
      event_id: {
        _in: user?.events,
      },
    };
  }
  const { data } = await client.query({
    query: GET_ORDER_AHEAD_LOCATIONS,
    variables: {
      where,
    },
  });
  return { orderAheadLocations: data.locations_order_ahead };
};

const StatusPill = ({
  label,
  tone,
}: {
  label: string;
  tone: "green" | "gray" | "blue" | "amber";
}) => {
  const tones = {
    green: "bg-green-100 text-green-800",
    gray: "bg-gray-100 text-gray-600",
    blue: "bg-blue-100 text-blue-800",
    amber: "bg-amber-100 text-amber-800",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`}
    >
      {label}
    </span>
  );
};

const LocationCard = ({ location }: { location: OrderAheadLocation }) => {
  const navigate = useNavigate();
  return (
    <div className="shadow sm:rounded-md bg-white px-4 py-4 mb-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/order-ahead-locations/${location.id}/edit`}
              className="font-semibold text-gray-900 hover:text-primary-600 hover:underline"
            >
              {location.name || "(unnamed)"}
            </Link>
            {location.is_active ? (
              <StatusPill label="Active" tone="green" />
            ) : (
              <StatusPill label="Inactive" tone="gray" />
            )}
            {location.is_pickup && <StatusPill label="Pickup" tone="blue" />}
            {location.location_type && (
              <StatusPill label={location.location_type} tone="amber" />
            )}
          </div>

          <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-500">
            <div>
              <dt className="inline font-medium text-gray-400">Vendor: </dt>
              <dd className="inline">
                {location.vendor?.name ? (
                  <Link
                    to={`/vendors/${location.vendor.id}/edit`}
                    className="text-gray-700 hover:text-primary-600 hover:underline"
                  >
                    {location.vendor.name}
                  </Link>
                ) : (
                  "—"
                )}
              </dd>
            </div>
            <div>
              <dt className="inline font-medium text-gray-400">Event: </dt>
              <dd className="inline">{location.event?.name || "—"}</dd>
            </div>
            <div>
              <dt className="inline font-medium text-gray-400">Service fee: </dt>
              <dd className="inline">{location.digital_surcharge_percentage ?? 0}%</dd>
            </div>
            <div>
              <dt className="inline font-medium text-gray-400">Tips: </dt>
              <dd className="inline">{location.is_tips ? "On" : "Off"}</dd>
            </div>
            <div>
              <dt className="inline font-medium text-gray-400">Processor: </dt>
              <dd className="inline">
                {location.payment_processor_config?.payment_processor || "—"}
              </dd>
            </div>
            {location.dynamic_descriptor && (
              <div>
                <dt className="inline font-medium text-gray-400">Descriptor: </dt>
                <dd className="inline">{location.dynamic_descriptor}</dd>
              </div>
            )}
          </dl>

          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-400">
            <div>ID #{location.id}</div>
            {location.user?.username && <div>Updated by {location.user.username}</div>}
            {location.updated_at && (
              <div>Updated {moment(location.updated_at).format("MMM D, YYYY h:mm a")}</div>
            )}
            {location.created_at && (
              <div>Created {moment(location.created_at).format("MMM D, YYYY")}</div>
            )}
          </div>
        </div>

        <SecondaryButton
          onClick={() => navigate(`/order-ahead-locations/${location.id}/edit`)}
        >
          Edit
        </SecondaryButton>
      </div>
    </div>
  );
};

const csvEscape = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const downloadCsv = (locations: OrderAheadLocation[]) => {
  const headers = [
    "ID",
    "Name",
    "Active",
    "Location Type",
    "Event",
    "Vendor",
    "Payment Processor",
    "Service Fee %",
    "Dynamic Descriptor",
    "Updated By",
    "Created Date",
    "Updated Date",
  ];
  const rows = locations.map((l) => [
    l.id,
    l.name,
    l.is_active ? "Yes" : "No",
    l.location_type,
    l.event?.name,
    l.vendor?.name,
    l.payment_processor_config?.payment_processor,
    l.digital_surcharge_percentage,
    l.dynamic_descriptor,
    l.user?.username,
    l.created_at ? moment(l.created_at).format("MM/DD/YYYY h:mm:ss a") : "",
    l.updated_at ? moment(l.updated_at).format("MM/DD/YYYY h:mm:ss a") : "",
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map(csvEscape).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const timestamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0];
  a.download = `Order_Ahead_Locations_${timestamp}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export const OrderAheadLocationList = () => {
  // Explicit check. The old `ItemsPermission` helper matched any value
  // containing "_ITEMS", so this gate was resolved by suffix, not by name.
  const { user } = usePermissions();
  const ItemsPermission = canManageOrderAhead(user);
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { orderAheadLocations } = useLoaderData() as {
    orderAheadLocations: OrderAheadLocation[];
  };
  const [isActiveToggle, setIsActiveToggle] = useState(true);

  const filtered = useMemo(
    () =>
      isActiveToggle
        ? orderAheadLocations.filter((l) => l.is_active)
        : orderAheadLocations,
    [orderAheadLocations, isActiveToggle]
  );

  // Group by vendor — mirrors the Order Ahead Overview UX.
  const vendorGroups = useMemo(() => {
    const map = new Map<string, { vendorName: string; locations: OrderAheadLocation[] }>();
    for (const loc of filtered) {
      const key = loc.vendor?.name || "Unknown vendor";
      const group = map.get(key) ?? { vendorName: key, locations: [] };
      group.locations.push(loc);
      map.set(key, group);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.vendorName.localeCompare(b.vendorName)
    );
  }, [filtered]);

  // Keep the URL search input in sync.
  const [searchValue, setSearchValue] = useState("");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setSearchValue(params.get("search") ?? "");
  }, []);

  return (
    <div className="px-2 pb-10">
      <div className="grid gap-2 grid-cols-6">
        <div className="col-span-3 mt-2">
          <Breadcrumbs />
        </div>
        <div className="col-span-3 mt-2 flex justify-end gap-2">
          <SecondaryButton onClick={() => downloadCsv(filtered)}>
            Export CSV
          </SecondaryButton>
          {ItemsPermission && (
            <PrimaryButton
              onClick={() => navigate("/order-ahead-locations/create")}
            >
              Create
            </PrimaryButton>
          )}
        </div>
      </div>

      <h1 className="mt-4 mb-4 text-xl font-semibold text-gray-900">
        Order Ahead Locations
      </h1>

      <div className="shadow sm:rounded-md bg-white px-4 py-3 mb-4 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <Input
            name="search"
            placeholder="Search by name"
            defaultValue={searchValue}
            onChange={(s) => {
              const term = typeof s === "string" ? s : "";
              navigate(
                term.length > 0
                  ? `/order-ahead-locations/list?search=${encodeURIComponent(term)}`
                  : `/order-ahead-locations/list`
              );
              revalidator.revalidate();
            }}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <span>Active only</span>
          <Switch
            checked={isActiveToggle}
            onChange={() => setIsActiveToggle((prev) => !prev)}
            onColor="#EE2E30"
            onHandleColor="#FFFFFF"
            handleDiameter={18}
            uncheckedIcon={false}
            checkedIcon={false}
            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
            height={20}
            width={48}
          />
        </label>
        <div className="text-sm text-gray-400">
          {filtered.length} of {orderAheadLocations.length} location
          {orderAheadLocations.length === 1 ? "" : "s"}
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="shadow sm:rounded-md bg-white px-6 py-10 text-center">
          <p className="font-medium text-gray-700">No locations found</p>
          <p className="mt-1 text-sm text-gray-500">
            {orderAheadLocations.length > 0
              ? "Try toggling Active Only off or clearing the search."
              : "Create one with the Create button above."}
          </p>
        </div>
      )}

      {vendorGroups.map((group) => (
        <section key={group.vendorName} className="mb-6">
          <h2 className="mb-2 text-lg font-semibold text-gray-800">
            {group.vendorName}
            <span className="ml-2 text-sm font-normal text-gray-400">
              ({group.locations.length} location
              {group.locations.length === 1 ? "" : "s"})
            </span>
          </h2>
          {group.locations.map((loc) => (
            <LocationCard key={loc.id} location={loc} />
          ))}
        </section>
      ))}
    </div>
  );
};
